import { action, observable } from "mobx";
import {
    TableViewStore,
    BaseListViewStore,
    BasicQueryCheckboxStore,
    BaasicDropdownStore,
    BasicQueryInputStore
} from 'core/stores';
import applicationContext from 'core/utils/applicationContext';
import { FacilityService } from 'application/facility/services';
import {CorporateService} from 'application/corporate/services';
import { PersonService } from 'application/person/services';
import { resolveColumns } from 'application/facility/util/resolveColumns';
import _ from 'lodash';
import { PatientDetailService } from 'application/import/services';
import React from "react";
import { ModalParams } from 'core/models';

@applicationContext
class FacilityViewStore extends BaseListViewStore {
    @observable accessor downloadUrl = null;
    @observable accessor downloadESignUrl = null;

    constructor(rootStore) {
        const service = new FacilityService(rootStore.application.baasic.apiClient);
        const personService = new PersonService(rootStore.application.baasic.apiClient);
        const corporateService = new CorporateService(rootStore.application.baasic.apiClient);

        super(rootStore, {
            name: 'facility',
            autoInit: true,
            authorization: {
                create: 'portalSection.create',
                update: 'portalSection.update'
            },
            routes:{
                edit: id => {
                    this.setChildNavigationTitle(i => i.id === id, item => item.name);
                    return this.rootStore.routerStore.goTo(
                        'master.app.main.facility.edit',
                        { id: id }
                    )
                },
                preview: id =>{
                    this.setChildNavigationTitle(i => i.id === id, item => item.name);
                    this.rootStore.routerStore.goTo(
                        'master.app.main.facility.preview',
                        { id: id }
                    );
                },
                schedule: id => {
                    this.setChildNavigationTitle(i => i.id === id, item => item.name);
                    this.rootStore.routerStore.goTo(
                        'master.app.main.facility.schedule',
                        { id: id }
                    );
                },
                create: () => {
                    this.rootStore.routerStore.goTo(
                        'master.app.main.facility.create',
                    );
                }
            },
            actions: () => {
                return {
                    find: async (params) => {
                        const parameters = { ...params };
                        parameters.inActive = params.inActive ? null : false;
                        parameters.isOnHold = !params.isOnHold ? null : true;
                        parameters.isNiko = false;
                        parameters.embed = "AccountRep,Corporate,State";
                        const response = await service.find(parameters);

                        _.extend(response.data,
                        {
                            item: _.map(response.data.item, (item => {
                                return _.extend(
                                    item, {
                                        accountRep: item.accountRep ? item.accountRep.firstName + ' ' + item.accountRep.lastName : '',         
                                        corporate:  item.corporate_ ? item.corporate_.name : '',
                                        state:  item.state && item.state.name,
                                        inActive: !item.inActive                       
                                    }
                                );
                            }))                         
                        });
                        this.onFindSuccess();

                        return response.data;
                    }
                }
            }
        });

        this.service = service;
        this.personService = personService;
        this.patientDetailService = new PatientDetailService(rootStore.application.baasic.apiClient);
        this.downloadRef = React.createRef();
        this.addressStore = new BasicQueryInputStore({});
        this.inactiveQueryCheckboxStore = new BasicQueryCheckboxStore();
        this.onHoldQueryCheckboxStore = new BasicQueryCheckboxStore();

        this.facilityAdditionalContactSearchModal = new ModalParams({});

        this.corporateStore = new BaasicDropdownStore({
            filterable: true
        }, {
            fetchFunc: async(term) => {
                const response = await corporateService.find({ isActive: true, searchQuery: term, rpp: 100 });             
                return response.data.item;          
             }
        });

        this.stateStore = new BaasicDropdownStore({
            filterable: true
        }, {
            fetchFunc: async(term) => {
                const response = await this.patientDetailService.findStates(term);
                return response.item;          
             }
        });

        this.accountRepStore = this.createPersonDropdownStore();

        this.queryUtility.onResetFilter = () => {
            this.inactiveQueryCheckboxStore.reset();
            this.onHoldQueryCheckboxStore.reset();

            this.corporateStore.setValue(null);
            this.corporateStore.filterTerm = "";

            this.accountRepStore.setValue(null);
            this.accountRepStore.filterTerm = "";

            this.stateStore.setValue(null);
            this.stateStore.filterTerm = "";

            this.addressStore.reset();
        }

        this.sync = async () => {
            this.loaderStore.suspend();                            
            this.tableStore.suspend();    
            try{
                await service.sync();
                await this.queryUtility.fetch();
                this.rootStore.notificationStore.success("Sync successfully done.");                                       
            }catch(err){
                this.rootStore.notificationStore.error(err && err.data && err.data.errors ? err.data.errors : 'Error occurred while trying to sync facilities done.');                                                       
            }        
            this.tableStore.resume();    
            this.loaderStore.resume();                                        
        };

        this.setTableStore(new TableViewStore(this.queryUtility, {
            columns: resolveColumns([
                {
                    key: 'name',
                    title: 'FACILITY.LIST.COLUMNS.NAME',
                    onClick:  item => this.routes.preview(item.id)
                },
                {
                    key: 'address',
                    title: 'FACILITY.LIST.COLUMNS.ADDRESS',
                    onClick:  item => this.routes.preview(item.id)
                },
                {
                    key: 'state',
                    title: 'FACILITY.LIST.COLUMNS.STATE',
                    onClick:  item => this.routes.preview(item.id),
                    width: 150,
                },
                {
                    key: 'phone',
                    title: 'FACILITY.LIST.COLUMNS.PHONE',
                    onClick:  item => this.routes.preview(item.id),
                    width: 150,
                },
                {
                    key: 'accountRep',
                    title: 'FACILITY.LIST.COLUMNS.ACCOUNT_REP',
                    onClick:  item => this.routes.preview(item.id)
                },
                {
                    key: 'corporate',
                    title: 'FACILITY.LIST.COLUMNS.CORPORATE',
                    onClick:  item => this.routes.preview(item.id),
                    width: 200,
                },
                {
                    key: 'masterPatientId',
                    title: 'FACILITY.LIST.COLUMNS.ACCOUNT_NUMBER',
                    onClick:  item => this.routes.preview(item.id),
                    width: 150,
                },
                {
                    key: 'inActive',
                    title: 'Is Active',
                    cell: (item) => {
                        return (
                            <td>
                                <div className="type--center">
                                    {item.dataItem.inActive ?        
                                        <i className="u-icon u-icon--sml u-icon--active"></i> :          
                                        <i className="u-icon u-icon--sml u-icon--inactive"></i>
                                    }
                                    {item.dataItem.isOnHold ?
                                        <i title="On Hold" className="u-icon u-icon--sml u-mar--left--tny u-icon--warning--yellow"></i> :          
                                        null
                                    }
                                </div>
                            </td>
                        );
                    },
                    width: 100,
                    headerClassName: "center"
                }         
            ]),
            actions: {
                onEdit: (item) => this.routes.edit(item.id),
                onPreview: (item) => this.routes.preview(item.id),
                onSchedule: (item) => this.routes.schedule(item.id)
            }
        }));
    }

    @action.bound
    filterOnChange(store) {
       store.filterTerm = "";
    }

    @action.bound
    createPersonDropdownStore() {
        return new BaasicDropdownStore({
            filterable: true
        }, {
            fetchFunc: async (term) => {
                const response = await this.personService.find({ searchQuery: term, rpp: 100, isActive:true });
                return _.map(response.data.item, item => {
                    return {
                        id: item.id,
                        name: item.firstName + ' ' + item.lastName                       
                    };
                });
            }
        });
    }
    @action.bound
    onFindSuccess() {
        const parameters = this.queryUtility.plainFilter;
        parameters.inActive = this.queryUtility.filter.inActive ? null : false;
        parameters.isNiko = false;
        this.downloadUrl = this.service.getDownloadUrl(parameters);
        this.downloadESignUrl = this.service.getDownloadESignUrl(parameters);
    }

    @action.bound
    async onDownloadESignReportModal() {
        window.open(
            `${this.downloadESignUrl}&contentDisposition=attachment&token=${
                this.rootStore.authStore.token.tokenUrl
            }&ttype=${
                this.rootStore.authStore.token.type}`
        );
    }

    @action.bound
    async onDownloadFacilityReport() {
        window.open(
            `${this.downloadUrl}&contentDisposition=attachment&token=${
                this.rootStore.authStore.token.tokenUrl
            }&ttype=${
                this.rootStore.authStore.token.type}`
        );
    }
    
    @action.bound
    async openFacilityAdditionalContactSearchModal() {
        const parameters = this.queryUtility.plainFilter;
        parameters.inActive = this.queryUtility.filter.inActive ? null : false;
        parameters.isNiko = false;

        this.facilityAdditionalContactSearchModal.open({
            parameters: parameters
        });
    }
}

export default FacilityViewStore;
